import { computed, ref } from 'vue';

import { useForm } from '@/utils/form/useForm';
import type {
  NavigationSection,
  UseSectionNavigation,
} from '@/utils/useSectionNavigator/types';

const unsortedRegisteredSections = ref<NavigationSection[]>([]);
const sectionErrors = ref<string[]>([]);
const genericSortingOrder = ref(1);
const visibleSection = ref<string | null>(null);

export const useSectionNavigator = (): UseSectionNavigation => {
  const addSection = (
    name: string,
    id: string,
    icon: string,
    sortingOrder: number,
    storePath: string,
    index?: number,
    showCheckIfSelected?: boolean,
  ): void => {
    unsortedRegisteredSections.value = unsortedRegisteredSections.value.filter(
      (section) => section.name !== name,
    );

    if (index !== undefined) {
      unsortedRegisteredSections.value.splice(index, 0, {
        name,
        id,
        icon,
        sortingOrder,
        showCheckIfSelected: showCheckIfSelected ?? false,
        storePath,
      });
    } else {
      unsortedRegisteredSections.value.push({
        name,
        id,
        icon,
        sortingOrder,
        showCheckIfSelected: showCheckIfSelected ?? false,
        storePath,
      });
    }
  };

  const removeSection = (id: string): void => {
    const index = unsortedRegisteredSections.value.findIndex(
      (section) => section.id === id,
    );
    if (index !== -1) {
      unsortedRegisteredSections.value.splice(index, 1);
    }
  };

  const goTo = (id: string): void => {
    const el = document.getElementById(`nav-section-${id}`);
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const addSectionError = (element: Element): void => {
    const section = element.closest('section.navigation');
    if (section) {
      sectionErrors.value.push(section.id.replace('nav-section-', ''));
    }
  };

  const showErrors = (errors: Record<string, string[]>): void => {
    sectionErrors.value = [];
    const errorKeys = Object.keys(errors);
    useForm().fields.forEach((field: string) => {
      if (errorKeys.includes(field)) {
        const containerEl = document.querySelector(`div[data-v="${field}"]`);

        if (containerEl) {
          addSectionError(containerEl);
        }
      }
    });

    // Check for custom errors as well
    const customErrors = document.querySelectorAll('div.custom-section-error');
    customErrors.forEach((errorEl) => {
      addSectionError(errorEl);
    });
  };

  const registeredSections = computed<NavigationSection[]>(() =>
    unsortedRegisteredSections.value.sort(
      (a, b) => a.sortingOrder - b.sortingOrder,
    ),
  );

  return {
    addSection,
    removeSection,
    goTo,
    registeredSections,
    showErrors,
    sectionErrors,
    visibleSection,
    genericSortingOrder,
  };
};
